<template>
  <UModal
    v-model:open="open"
    :title="title"
    :description="description"
    :content="{ dir: 'rtl' }"
    :ui="{
      overlay: 'z-[80]',
      content: [
        'z-[80] sm:max-w-2xl',
        // Phones get an action sheet that rises from the bottom edge; desktop keeps the centered dialog.
        'max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:max-h-[92dvh]',
        'max-sm:data-[state=open]:animate-[slide-in-from-bottom_250ms_ease-out] max-sm:data-[state=closed]:animate-[slide-out-to-bottom_200ms_ease-in]',
      ].join(' '),
      header: 'items-start gap-3 p-4 sm:p-6',
      title: 'text-base sm:text-lg font-bold',
      description: 'text-xs sm:text-sm',
      body: 'p-4 sm:p-6 overflow-x-hidden',
      footer: flow.showStepNav ? 'block border-t border-default p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6' : 'hidden',
    }"
  >
    <template #title>
      <span class="flex items-center gap-2">
        <UIcon
          :name="flow.step === 'success' ? 'i-lucide-calendar-check' : 'i-lucide-calendar-plus'"
          class="size-5 shrink-0 text-primary"
        />
        <span class="truncate">{{ title }}</span>
      </span>
    </template>

    <template #body>
      <div class="space-y-4">
        <div v-if="flow.step !== 'success'">
          <div class="flex items-center gap-1.5">
            <div
              v-for="(item, index) in flow.modalStepLabels"
              :key="item.id"
              class="h-1 flex-1 rounded-full transition-colors"
              :class="index <= flow.stepIndex ? 'bg-primary' : 'bg-accented'"
            />
          </div>
          <!-- Five labels don't fit on a phone, so the header counter carries them there. -->
          <div class="mt-2 hidden justify-between text-[11px] sm:flex">
            <span
              v-for="(item, index) in flow.modalStepLabels"
              :key="item.id"
              class="min-w-0 flex-1 truncate px-0.5 text-center transition-colors"
              :class="index === flow.stepIndex
                ? 'font-semibold text-primary'
                : index < flow.stepIndex ? 'text-default' : 'text-dimmed'"
            >
              {{ item.label }}
            </span>
          </div>
        </div>

        <p
          v-if="flow.stepHint && flow.step !== 'success'"
          class="flex items-start gap-1.5 text-xs text-muted"
        >
          <UIcon
            name="i-lucide-info"
            class="mt-0.5 size-3.5 shrink-0"
          />
          {{ flow.stepHint }}
        </p>

        <UAlert
          v-if="flow.error"
          color="error"
          variant="subtle"
          icon="i-lucide-triangle-alert"
          :title="flow.error"
          :close="{ color: 'neutral', variant: 'link' }"
          @update:open="flow.error = ''"
        />

        <div
          v-if="flow.loading && flow.step !== 'success'"
          class="space-y-3"
        >
          <USkeleton class="h-16 w-full" />
          <USkeleton class="h-16 w-full" />
        </div>

        <BookingFlowSteps
          v-else
          :flow="flow"
          success-title="نوبت شما ثبت شد"
          success-description="پیامک تأیید به شماره شما ارسال می‌شود."
        />
      </div>
    </template>

    <template #footer>
      <BookingFlowNav :flow="flow" />
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { useBookingFlow } from '~/composables/useBookingFlow'
import { useBookingRequest } from '~/composables/useBookingRequest'
import BookingFlowSteps from '~/components/blocks/BookingFlowSteps.vue'
import BookingFlowNav from '~/components/blocks/BookingFlowNav.vue'

const { open, service } = useBookingRequest()
const flow = useBookingFlow()

const title = computed(() => flow.selectedService?.title || 'دریافت نوبت')

const description = computed(() => {
  if (flow.step === 'success') return 'رزرو با موفقیت انجام شد.'
  const counter = `مرحلهٔ ${flow.toFaDigits(flow.stepNumber)} از ${flow.toFaDigits(flow.stepTotal)}`
  return flow.stepTitle ? `${counter} · ${flow.stepTitle}` : counter
})

watch(
  () => [open.value, service.value] as const,
  async ([isOpen, next]) => {
    if (!isOpen) {
      flow.reset()
      return
    }
    if (!next) return
    flow.reset()
    await flow.startWithService(next)
  },
)
</script>
